"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { FiMenu, FiX, FiMail, FiPhone } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import Image from "next/image";
import { site, trackContact, whatsappHref } from "../../lib/site";
import { industries } from "../../lib/industries";
import { servicePages } from "../../lib/servicePages";
import LoadingBar from "react-top-loading-bar";

type MenuItem = {
  name: string;
  href: string;
  icon?: React.ReactNode;
  submenu?: Array<SubMenuItem | ProductGroup>;
  /** Present when the submenu renders as a full-width grouped mega menu. */
  mega?: { title: string; subtitle: string; allHref: string; allLabel: string; dense?: boolean };
};

type SubMenuItem = {
  name: string;
  href: string;
  icon?: React.ReactNode;
};

type ProductGroup = {
  group: string;
  items: Array<ProductItem>;
};

type ProductItem = {
  name: string;
  href: string;
  description: string;
  icon?: string; // Added icon property
};

// Services mega menu: the four SAP Business One service pages plus the six service areas on the services page, so the menu matches the page.
const serviceSubmenu: ProductGroup[] = [
  {
    group: "SAP Business One services",
    items: servicePages.map((page) => ({ name: page.name, href: `/services/${page.slug}`, description: page.kicker, icon: page.image })),
  },
  {
    group: "Other services",
    items: [
      { name: "Custom software development", href: "/services#software-development", description: "Focused applications for a specific workflow" },
      { name: "IT consulting & solution mapping", href: "/services#it-consulting", description: "Product fit and priorities before you decide" },
      { name: "Cloud solutions & migration", href: "/services#cloud-solutions", description: "What moves, what stays connected, how you access it" },
      { name: "Reporting & data analytics", href: "/services#data-analytics", description: "Reports built from the decisions you make" },
      { name: "System integration", href: "/services#system-integration", description: "Connect the applications you already run" },
      { name: "Support & maintenance", href: "/services#support", description: "Issues, changes, and ongoing help" },
    ],
  },
];

// Industries mega menu, grouped for the Africa-first audience. Content comes from lib/industries so it stays in step with the pages.
const industryGroups: Array<{ group: string; slugs: string[] }> = [
  { group: "Logistics & trade", slugs: ["depot-and-containers", "fleet-and-transport", "commodity-export", "storage-services"] },
  { group: "Manufacturing & mining", slugs: ["beverages-and-breweries", "edible-oil-manufacturing", "cement-manufacturing", "steel-manufacturing", "food-processing", "mining-and-quarrying"] },
  { group: "Distribution & retail", slugs: ["fmcg-distribution", "field-distribution", "retail-billing"] },
  { group: "Agriculture & services", slugs: ["agriculture", "poultry-farming", "rental-services"] },
];
const industrySubmenu: ProductGroup[] = industryGroups.map(({ group, slugs }) => ({
  group,
  items: slugs
    .map((slug) => industries.find((item) => item.slug === slug))
    .filter((item): item is (typeof industries)[number] => Boolean(item))
    .map((item) => ({ name: item.name, href: `/industries/${item.slug}`, description: item.kicker, icon: item.image })),
}));

const navigation: MenuItem[] = [
  { name: "Home", href: "/" },
  {
    name: "About Us",
    href: "/about",
    submenu: [
      { name: "About Company", href: "/about" },
      { name: "Where we work", href: "/where-we-work" },
      { name: "Contact Us", href: "/contact" },
    ],
  },
  {
    name: "Services",
    href: "/services",
    mega: { title: "Services", subtitle: "SAP Business One implementation, support, migration, and training, plus custom software, integration, and reporting", allHref: "/services", allLabel: "View all services", dense: true },
    submenu: serviceSubmenu,
  },
  {
    name: "Industries",
    href: "/industries",
    mega: { title: "Solutions by industry", subtitle: "Worked operational flows for businesses across Africa and India", allHref: "/industries", allLabel: "View all industries", dense: true },
    submenu: industrySubmenu,
  },
  {
    name: "Products",
    href: "/products",
    mega: { title: "Our Product Offerings", subtitle: "Powerful solutions to transform your business", allHref: "/products", allLabel: "View all products" },
    submenu: [
      {
        group: "Enterprise Solutions",
        items: [
          {
            name: "SAP Business One",
            href: "/products/sap",
            description: "Manufacturing, trading, and services",
            icon: "/static/img/services/sap-business-one.png",
          },
          {
            name: "SAP S/4HANA Public Cloud",
            href: "/products/sap-s4-public-cloud",
            description: "Cloud ERP, kept current by SAP",
            icon: "/static/img/products/sap-s4-public-cloud.svg",
          },
          {
            name: "LITFusion",
            href: "/products/lit-fusion",
            description: "Roadside sales for distributors",
            icon: "/static/img/products/lit-fusion.png",
          },
          {
            name: "LITCRM",
            href: "/products/lit-crm",
            description: "Leads, calls, email, and meetings",
            icon: "/static/img/products/lit-crm.png",
          },
        ],
      },
      {
        group: "Management Systems",
        items: [
          {
            name: "LITKHATA",
            href: "/products/lit-khata",
            description: "Invoices, cash, and inventory for shops",
            icon: "/static/img/products/lit-khata.png",
          },
          {
            name: "LITWay",
            href: "/products/lit-way",
            description: "Vehicle profile for truck owners",
            icon: "/static/img/products/lit-way.png",
          },
          {
            name: "ICD Management",
            href: "/products/icd",
            description: "Port and container depot operations",
            icon: "/static/img/products/icd.png",
          },
        ],
      },
    ],
  },
  { name: "Careers", href: "/careers" },
  { name: "Training", href: "/training" },
  //{
  //  name: "Resources",
  //  href: "#",
  //  submenu: [
  //    { name: "Careers", href: "/careers" },
  //    { name: "Training", href: "/training" },
  //    { name: "Blog", href: "/blog" },
  //  ],
  //},
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [submenuTimeout, setSubmenuTimeout] = useState<NodeJS.Timeout | null>(null);
  const pathname = usePathname();
  const router = useRouter();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveSubmenu(null);
  }, [pathname]);

  // Handle route changes for loading bar
  useEffect(() => {
    setProgress(30);
    const timer = setTimeout(() => {
      setProgress(100);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [pathname]);

  const handleSubmenuOpen = (idx: number) => {
    if (submenuTimeout) {
      clearTimeout(submenuTimeout);
      setSubmenuTimeout(null);
    }
    setActiveSubmenu(idx);
  };

  const handleSubmenuClose = () => {
    const timeout = setTimeout(() => {
      setActiveSubmenu(null);
    }, 300); // 300ms delay before closing
    setSubmenuTimeout(timeout);
  };

  // Check if a navigation item should be highlighted as active
  const isActiveLink = (item: MenuItem): boolean => {
    if (item.href === pathname) return true;

    if (item.submenu) {
      for (const subitem of item.submenu) {
        if ("href" in subitem && subitem.href === pathname) return true;
        if ("items" in subitem) {
          for (const groupItem of subitem.items) {
            if (groupItem.href === pathname) return true;
          }
        }
      }
    }
    return false;
  };

  // Function to check if a menu item is a product group
  const isProductGroup = (item: any): item is ProductGroup => {
    return item && "group" in item && "items" in item;
  };

  return (
    <>
      <LoadingBar color="#6cb2de" progress={progress} onLoaderFinished={() => setProgress(0)} height={3} shadow={true} />
      <header className={`site-header sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? "bg-white shadow-md py-2" : "bg-white/90 py-4"}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            {/* Logo */}
            <Link href="/" className="flex shrink-0 items-center gap-2">
              <Image src="/static/img/logo.png" alt="" width={40} height={40} priority className="h-10 w-10 shrink-0 object-contain" />
              <span className="font-bold leading-tight text-brand-900 text-sm sm:text-base xl:text-lg">Link Ideas Technologies Pvt Ltd</span>
            </Link>

            <div className="hidden items-center gap-3 lg:flex">
            {/* Desktop Navigation */}
            <nav className="flex items-center gap-x-2 xl:gap-x-3">
              {navigation.map((item, idx) => (
                <div key={item.name} className="relative group" onFocus={() => item.submenu && handleSubmenuOpen(idx)} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setActiveSubmenu(null); }} onKeyDown={(event) => { if (event.key === "Escape") { setActiveSubmenu(null); event.stopPropagation(); } }}>
                  <Link href={item.href} className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 ${isActiveLink(item) ? "border-brand-400 text-brand-900" : "border-transparent text-gray-700 hover:text-brand-900 hover:border-brand-300"}`} onMouseEnter={() => item.submenu && handleSubmenuOpen(idx)} onMouseLeave={() => item.submenu && handleSubmenuClose()}>
                    {item.icon && item.icon}
                    {item.name}
                    {item.submenu && (
                      <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </Link>

                  {/* Regular Submenu */}
                  {item.submenu && !item.mega && activeSubmenu === idx && (
                    <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 transform" onMouseEnter={() => handleSubmenuOpen(idx)} onMouseLeave={() => handleSubmenuClose()}>
                      {item.submenu.map(
                        (subitem, subIdx) =>
                          !isProductGroup(subitem) && (
                            <Link key={subIdx} href={subitem.href} className={`flex items-center px-4 py-2 text-sm ${pathname === subitem.href ? "bg-brand-50 text-brand-700" : "text-gray-700 hover:bg-brand-50 hover:text-brand-700"}`}>
                              {subitem.icon && subitem.icon}
                              {subitem.name}
                            </Link>
                          )
                      )}
                    </div>
                  )}

                  {/* Mega menu (Products, Industries) */}
                  {item.submenu && item.mega && activeSubmenu === idx && (
                    <div className="fixed left-4 right-4 mx-auto max-h-[75vh] overflow-y-auto w-auto max-w-6xl mt-2 bg-white shadow-2xl rounded-lg overflow-hidden z-10 transition-all duration-300 ease-in-out" style={{ maxWidth: "calc(100vw - 4rem)" }} onMouseEnter={() => handleSubmenuOpen(idx)} onMouseLeave={() => handleSubmenuClose()}>
                      <div className="bg-brand-50/50 px-8 py-4 border-b border-gray-100">
                        <h2 className="text-brand-900 text-xl font-semibold">{item.mega.title}</h2>
                        <p className="text-gray-600">{item.mega.subtitle}</p>
                      </div>
                      <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${item.mega.dense ? `p-6 ${item.submenu.filter(isProductGroup).length >= 4 ? "xl:grid-cols-4" : "xl:grid-cols-2"}` : "p-8"}`}>
                        {item.submenu.map(
                          (group, groupIdx) =>
                            isProductGroup(group) && (
                              <div key={groupIdx} className="col-span-1">
                                <h3 className="font-semibold text-brand-900 text-lg mb-4 flex items-center">
                                  <span className="inline-block w-1.5 h-5 bg-brand-500 rounded-sm mr-2"></span>
                                  {group.group}
                                </h3>
                                <div className={item.mega?.dense ? "space-y-1" : "space-y-6"}>
                                  {group.items.map((subitem, subItemIdx) => (
                                    <Link key={subItemIdx} href={subitem.href} className={`flex items-start group rounded-lg transition-all duration-200 hover:bg-brand-50 ${item.mega?.dense ? "p-2" : "p-3"} ${pathname === subitem.href ? "bg-brand-50/70" : ""}`}>
                                      {subitem.icon && (
                                        <div className={`flex-shrink-0 mr-3 overflow-hidden rounded-md ${item.mega?.dense ? "h-9 w-12" : "h-10 w-10 mr-4"}`}>
                                          <Image src={subitem.icon} alt="" width={96} height={64} className={item.mega?.dense ? "h-full w-full object-cover" : "h-full w-full object-contain"} />
                                        </div>
                                      )}
                                      <div>
                                        <div className={`font-medium text-brand-900 group-hover:text-brand-700 ${item.mega?.dense ? "text-sm" : "text-base"}`}>{subitem.name}</div>
                                        <p className={`text-gray-500 group-hover:text-brand-600 ${item.mega?.dense ? "text-xs mt-0.5" : "text-sm mt-1"}`}>{subitem.description}</p>
                                      </div>
                                      <div className="ml-auto self-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <svg className="h-5 w-5 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            )
                        )}
                      </div>
                      <div className="bg-gray-50 px-8 py-3 border-t border-gray-100">
                        <Link href={item.mega.allHref} className="text-brand-600 hover:text-brand-800 text-sm font-medium flex items-center group">
                          {item.mega.allLabel}
                          <svg className="ml-1 h-4 w-4 group-hover:ml-2 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Contact Button */}
            <div className="flex items-center gap-1">
              <a href={`tel:${site.phoneTel}`} onClick={() => trackContact("call")} className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 hover:bg-brand-50" aria-label={`Call ${site.phoneDisplay}`}>
                <FiPhone />
              </a>
              <a href={whatsappHref("Hello LITPL, I want to discuss a business requirement.")} onClick={() => trackContact("whatsapp")} target="_blank" rel="noopener noreferrer" className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 hover:bg-brand-50" aria-label="Chat with LITPL on WhatsApp">
                <FaWhatsapp />
              </a>
              <Link href="/contact#enquiry" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500">
                <FiMail className="mr-2" />
                Let’s talk
              </Link>
            </div>
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden">
              <button type="button" className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-brand-500 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500" aria-expanded={mobileMenuOpen} aria-controls="mobile-navigation" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <span className="sr-only">{mobileMenuOpen ? "Close main menu" : "Open main menu"}</span>
                {mobileMenuOpen ? <FiX className="block h-6 w-6" aria-hidden="true" /> : <FiMenu className="block h-6 w-6" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div id="mobile-navigation" hidden={!mobileMenuOpen} className="lg:hidden max-h-[calc(100dvh-80px)] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white shadow-lg">
            {navigation.map((item) => (
              <div key={item.name}>
                <Link href={item.href === "#" ? (item.submenu && !isProductGroup(item.submenu[0]) ? item.submenu[0].href : "/") : item.href} className={`block px-3 py-2 rounded-md text-base font-medium ${isActiveLink(item) ? "bg-brand-50 text-brand-700" : "text-gray-700 hover:bg-brand-50 hover:text-brand-700"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {item.icon && item.icon}
                      {item.name}
                    </div>
                    {item.submenu && (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </Link>

                {/* Mobile Submenu */}
                {item.submenu && (
                  <div className="pl-4 space-y-1 mt-1">
                    {item.mega
                      ? // Grouped items (Products, Industries)
                        item.submenu.map(
                          (group, groupIdx) =>
                            isProductGroup(group) && (
                              <div key={groupIdx} className="mb-2">
                                <p className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">{group.group}</p>
                                {group.items.map((subitem, subItemIdx) => (
                                  <Link key={subItemIdx} href={subitem.href} className={`block px-3 py-2 rounded-md text-sm font-medium ${pathname === subitem.href ? "bg-brand-50 text-brand-700" : "text-gray-600 hover:bg-brand-50 hover:text-brand-700"}`}>
                                    {subitem.name}
                                  </Link>
                                ))}
                              </div>
                            )
                        )
                      : // For other items, show regular submenu
                        item.submenu.map(
                          (subitem, subIdx) =>
                            !isProductGroup(subitem) && (
                              <Link key={subIdx} href={subitem.href} className={`block px-3 py-2 rounded-md text-sm font-medium ${pathname === subitem.href ? "bg-brand-50 text-brand-700" : "text-gray-600 hover:bg-brand-50 hover:text-brand-700"}`}>
                                {subitem.name}
                              </Link>
                            )
                        )}
                  </div>
                )}
              </div>
            ))}

            <Link href="/contact" className="block w-full text-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 mt-4">
              <FiMail className="inline-block mr-2" />
              Let’s talk
            </Link>
          </div>
        </div>
      </header>
    </>
  );
}
